// ABOUTME: An alternate Material-UI theme, structurally identical to theme.ts with a different palette
// ABOUTME: Proves the theme-swapping mechanism works; not a designed brand theme (see #1886/#1887 scope)

import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      secondary: string;
    };
    behaviorHeart: string;
    oneOnOneAccent: string;
    destructiveAccent: string;
  }

  interface PaletteOptions {
    gradient?: {
      primary?: string;
      secondary?: string;
    };
    behaviorHeart?: string;
    oneOnOneAccent?: string;
    destructiveAccent?: string;
  }

  interface Theme {
    logoSrc: string;
  }
  interface ThemeOptions {
    logoSrc?: string;
  }
}

export const altTheme = createTheme({
  logoSrc: '/lets-reset-logo-108.png',
  palette: {
    primary: {
      main: '#4F46E5', // Indigo (alt theme accent)
      light: '#818CF8',
      dark: '#3730A3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0D9488', // Teal
      light: '#5EEAD4',
      dark: '#115E59',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Light gray background
      paper: '#ffffff',
    },
    gradient: {
      primary: 'linear-gradient(135deg, #4F46E5 0%, #0D9488 100%)',
      secondary: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    },
    // Pink for the "Heart" behavior type — distinct from `success` (used for the "Star" type)
    behaviorHeart: '#E91E63',
    oneOnOneAccent: '#00BCD4',
    destructiveAccent: '#FF5722',
    success: {
      main: '#10B981', // Green for positive behaviors (stars)
      light: '#34D399',
      dark: '#047857',
      contrastText: '#000000', // white text fails WCAG AA here (2.54:1); black passes (~8.3:1)
    },
    warning: {
      main: '#F59E0B', // Orange/yellow for warnings (triangles)
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#000000', // white text fails WCAG AA here (2.15:1); black passes (~9.8:1)
    },
    error: {
      main: '#EF4444', // Red for consequences (squares)
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#3B82F6', // Blue for information
      light: '#60A5FA',
      dark: '#1D4ED8',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none', // Disable ALL CAPS on buttons
    },
  },
  shape: {
    borderRadius: 12, // Rounded corners for modern look
  },
  spacing: 8, // 8px base spacing unit
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: '1rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#4A90E2',
            },
            // Fix double focus outline on password fields with visibility toggle
            '&.Mui-focused': {
              '& input': {
                outline: 'none',
                border: 'none',
                boxShadow: 'none',
              },
              '& .MuiInputAdornment-root': {
                // Ensure adornment area doesn't create separate borders
                border: 'none',
                outline: 'none',
              },
            },
            // Also handle non-focused state to prevent any border artifacts
            '& input': {
              border: 'none',
              boxShadow: 'none',
              '&:focus': {
                outline: 'none',
                border: 'none',
                boxShadow: 'none',
              },
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
        },
        elevation3: {
          boxShadow: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.06)',
          '&:hover': {
            boxShadow: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,
        },
      },
    },
  },
});

export default altTheme;
